/**
 * SprintRayOrderForm - Main Container Component
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Header } from './Header';
import { CustomerInfo } from './CustomerInfo';
import { Products } from './Products';
import { Promotions } from './Promotions';
import { FreeResins } from './FreeResins';
import { FreePackages } from './FreePackages';
import { SignaturePadComponent } from './SignaturePad';
import { CartSummary } from './CartSummary';
import { OrderPrintTemplate } from './OrderPrintTemplate';
import { PastOrdersButton } from './PastOrdersButton';
import { PastOrdersModal } from './PastOrdersModal';
import { getAPIService } from './api';
import { offlineStorage } from './storage';
import {
	CustomerInfo as CustomerInfoDataType,
	CartItem,
	SelectedFreeResin,
	SelectedPackage,
	FormState,
	SprintRayOrderFormProps,
	OrderPayload,
	UIText,
	Product,
	Promotion,
	Distributor,
	Campaign,
	CompletedOrder,
} from './types';
import { getTranslationsWithOverrides, Language } from './translations';
import {
	PRODUCTS,
	PROMOTIONS,
	MAX_FREE_RESINS_BUDGET,
	FREE_PACKAGE_OPTIONS,
	ALL_PROMOTIONS,
} from './constants';
import {
	localizeProducts,
	localizePromotions,
} from './localization';
import { COUNTRY_CATALOG } from './countryCatalog';
import { getDealersForCountryName, isDistribuidorValidForCountry } from './countryMatching';
import { formatPrice, getEventCurrency, getVatDisplayEnabled, getVatRate, calculateGrossPrice, generateClientOrderId } from './utils';
import {
	canPromotionBeAdded,
	isTradeUpPromotion,
	promotionHasPackageMode,
} from './promotionRules';

// Import react-to-print properly
import { useReactToPrint } from 'react-to-print';

const SprintRayOrderForm: React.FC< SprintRayOrderFormProps > = ( props ) => {
	// Determine initial language from props or default to 'de'
	const initialLang = ( props.resolved_lang || props.lang || 'de' ) as Language;
	const initialTranslations = getTranslationsWithOverrides(
		initialLang,
		window.sprintRayFormSettings?.translationOverrides
	);

	// Get initial products from server (already translated) or fallback to hardcoded
	const serverProducts = window.sprintRayFormSettings?.initialProducts || [];
	const initialProducts = serverProducts.length > 0
		? serverProducts
		: PRODUCTS;

	// Get promotions from server (already translated) or fallback to hardcoded
	const serverPromotions = window.sprintRayFormSettings?.initialPromotions || [];
	const initialPromotions = serverPromotions.length > 0
		? serverPromotions
		: ALL_PROMOTIONS;

	// Get initial partners (master-dealer-hub) from server
	const serverDistributors = window.sprintRayFormSettings?.initialPartners || [];
	const initialDistributors: Distributor[] = serverDistributors.length > 0
		? serverDistributors
		: [];

	// True only when this event has an explicit, non-empty dealer restriction
	// (set via the event's "Partner Selection" admin meta box). When restricted,
	// the frontend shows exactly that fixed dealer list regardless of country;
	// when not, dealers are filtered by the customer's selected country instead.
	const isPartnerSelectionRestricted = window.sprintRayFormSettings?.isPartnerSelectionRestricted || false;

	// Full static world-country catalog — always offered in full, in both the
	// restricted and unrestricted case (country selection never narrows this list,
	// only which dealers are shown depends on the restriction).
	const countryOptions: { code: string; label: string }[] = COUNTRY_CATALOG.map(
		( c ) => ( { code: c.code, label: c.name } )
	);

	// Get campaigns from server (if enabled for this event)
	const serverCampaigns = window.sprintRayFormSettings?.initialCampaigns || [];
	const serverCampaignsEnabled = window.sprintRayFormSettings?.campaignsEnabled || false;
	const initialCampaigns: Campaign[] = serverCampaigns.length > 0
		? serverCampaigns
		: [];

	// Debug logging for campaigns
	console.log( '📋 [FORM] Campaigns from server:', {
		enabled: serverCampaignsEnabled,
		count: serverCampaigns.length,
		campaigns: serverCampaigns,
	} );

	// Get event title from server or use prop
	const eventTitle = window.sprintRayFormSettings?.eventTitle || props.title || 'EXPODENTAL';

	// Get promo validity from server
	const promoValidity = window.sprintRayFormSettings?.promoValidity || '';

	// Get event date range from server
	const eventDateRange = window.sprintRayFormSettings?.eventDateRange || '';

	// Dynamic data state (with fallback to hardcoded data)
	const [ products, setProducts ] = useState< Product[] >( initialProducts );
	const [ translations, setTranslations ] = useState< UIText >( initialTranslations );
	const [ promotions, setPromotions ] = useState< Promotion[] >( initialPromotions );
	const [ distributors, setDistributors ] = useState< Distributor[] >( initialDistributors );
	const [ campaignOptions ] = useState< Campaign[] >( initialCampaigns );
	const [ campaignsEnabled ] = useState< boolean >( serverCampaignsEnabled );

	// Filter products that can be used as free resins
	const freeResinProducts = products.filter( ( p ) => p.freeResinValue && p.freeResinValue > 0 );

	// Extract combos from promotions (combos are now part of promotion data)
	const combos = promotions.filter( p => p.freeResinCombo ).map( p => ( {
		id: p.id + '-combo',
		databaseId: p.databaseId,
		name: p.title + ' Combo',
		budget: p.freeResinCombo!.budget,
		products: p.freeResinCombo!.products,
		totalValue: p.freeResinCombo!.products.reduce( (sum, prod ) => sum + ( prod.value * prod.quantity ), 0 ),
		active: true,
	} ) );

	// Form State
	const [ customerInfo, setCustomerInfo ] = useState< CustomerInfoDataType >(
		{
			distribuidor: '',
			nombreFachberater: '',
			nombre: '',
			apellidos: '',
			direccion: '',
			ciudad: '',
			nombreClinicaLaboratorio: '',
			telefono: '',
			email: '',
			codigoPostal: '',
			pais: '',
			requestType: 'quote',
			hubspotCampaign: '',
			hubspotCampaignName: '',
		}
	);

	const [ cartItems, setCartItems ] = useState< CartItem[] >( [] );
	const [ appliedPromotions, setAppliedPromotions ] = useState< Promotion[] >(
		[]
	);
	const [ selectedFreeResins, setSelectedFreeResins ] = useState<
		SelectedFreeResin[]
	>( [] );
	const [ selectedPackage, setSelectedPackage ] =
		useState< SelectedPackage | null >( null );
	const [ signature, setSignature ] = useState< string >( '' );
	const [ remarks, setRemarks ] = useState< string >( '' );

	// UI State
	const [ isOffline, setIsOffline ] = useState< boolean >(
		! navigator.onLine
	);
	const [ hasUnsyncedOrders, setHasUnsyncedOrders ] =
		useState< boolean >( false );
	const [ pendingOrdersCount, setPendingOrdersCount ] =
		useState< number >( 0 );
	const [ isSyncing, setIsSyncing ] = useState< boolean >( false );
	const [ syncingCount, setSyncingCount ] = useState< number >( 0 );
	const [ isSubmitting, setIsSubmitting ] = useState< boolean >( false );
	const [ submitMessage, setSubmitMessage ] = useState< {
		type: 'success' | 'error';
		text: string;
	} | null >( null );
	const [ showOfflineIndicator, setShowOfflineIndicator ] =
		useState< boolean >( false );
	const [ validationErrors, setValidationErrors ] = useState< {
		distribuidor?: boolean;
		nombreFachberater?: boolean;
		nombre?: boolean;
		apellidos?: boolean;
		direccion?: boolean;
		ciudad?: boolean;
		nombreClinicaLaboratorio?: boolean;
		telefono?: boolean;
		email?: boolean;
		codigoPostal?: boolean;
		pais?: boolean;
		signature?: boolean;
		cart?: boolean;
	} >( {} );
	const [ clearSignatureTrigger, setClearSignatureTrigger ] =
		useState< boolean >( false );
	const [ isResetting, setIsResetting ] = useState< boolean >( false );

	// Dealers to offer in the trade-partner dropdown: when the event has a fixed
	// dealer restriction, always show that full list regardless of country; when
	// it doesn't, filter the (unrestricted) dealer pool by the selected country.
	const filteredDistributors = useMemo( () => {
		if ( isPartnerSelectionRestricted ) {
			return distributors;
		}
		return customerInfo.pais ? getDealersForCountryName( distributors, customerInfo.pais ) : [];
	}, [ isPartnerSelectionRestricted, distributors, customerInfo.pais ] );

	// True when the dealer field is showing the free-text fallback (no dealer
	// exists for the selected country) rather than a dropdown-picked value —
	// single source of truth shared by CustomerInfo's rendering and the
	// submitted payload (see handleSubmit), so HubSpot sync can tell them apart.
	const isDistribuidorManual = useMemo(
		() => ! isPartnerSelectionRestricted && !! customerInfo.pais && filteredDistributors.length === 0,
		[ isPartnerSelectionRestricted, customerInfo.pais, filteredDistributors ]
	);

	// Promotion Validation State
	const [ promotionErrors, setPromotionErrors ] = useState<
		Record< string, string >
	>( {} );

	// Past Orders State
	const [ pastOrders, setPastOrders ] = useState< CompletedOrder[] >( [] );
	const [ showPastOrdersModal, setShowPastOrdersModal ] = useState< boolean >( false );

	// Print functionality state
	const contentRef = useRef< HTMLDivElement >( null );
	const printShownTimestampRef = useRef< number >( 0 );
	// Synchronous re-entrancy guard for handleSubmit — isSubmitting (state)
	// only takes effect on the next render, so a fast double-click/tap can
	// fire handleSubmit twice before the button disables.
	const isSubmittingRef = useRef< boolean >( false );
	const [ lastSubmittedOrder, setLastSubmittedOrder ] = useState<
		OrderPayload | null
	>( null );
	const [ showPrintButton, setShowPrintButton ] = useState< boolean >( false );

	// Print handler using react-to-print - must be called at top level
	// Using preserveAfterPrint and custom print function for mobile/iOS compatibility
	const printFn = useReactToPrint( {
		contentRef: contentRef,
		documentTitle: `SprintRay-Quote`,
		preserveAfterPrint: true,
		print: ( printIframe ) =>
			new Promise< void >( ( resolve ) => {
				// Hide iframe immediately after triggering print
				printIframe.style.display = 'none';
				printIframe.contentWindow?.print();
				resolve();
			} ),
		onPrintError: ( error: any ) => {
			console.error( 'Print error:', error );
		},
		onAfterPrint: () => {
			// Auto-dismiss print section after printing
			console.log( '🖨️ [FORM] Print completed, dismissing print section' );
			setLastSubmittedOrder( null );
			setShowPrintButton( false );
			printShownTimestampRef.current = 0;
		},
	} );

	// Handle print button click
	const handlePrintClick = useCallback( () => {
		printFn();
	}, [ printFn ] );

	// Track if user has started filling a new order after successful submission
	// Auto-dismiss print section when form changes are detected
	useEffect( () => {
		// Only check if print section is visible
		if ( ! showPrintButton || printShownTimestampRef.current === 0 ) {
			return;
		}

		// Check if any customer info has been filled (non-empty)
		const hasCustomerInfo = Object.values( customerInfo ).some(
			( value ) => value && value.trim() !== ''
		);
		const hasCartItems = cartItems.length > 0;
		const hasPromotions = appliedPromotions.length > 0;
		const hasSignature = signature !== '';

		// If user has started filling the form again, dismiss print section
		if ( hasCustomerInfo || hasCartItems || hasPromotions || hasSignature ) {
			console.log( '📝 [FORM] User started new order, dismissing print section' );
			setLastSubmittedOrder( null );
			setShowPrintButton( false );
		}
	}, [ customerInfo, cartItems, appliedPromotions, signature, showPrintButton ] );

	// Allow user to dismiss the print section manually
	const handleDismissPrintSection = useCallback( () => {
		setLastSubmittedOrder( null );
		setShowPrintButton( false );
		printShownTimestampRef.current = 0;
	}, [] );

	// Check if free resins should be enabled
	const isFreeResinsEnabled = appliedPromotions.some( ( p ) => p.includesFreeResins === true );

	// Check if any NON-TRADE-UP promotion has package mode enabled
	// Trade-Up promotions are not eligible for free packages
	const hasPackageMode = appliedPromotions.some(
		( p ) => ! isTradeUpPromotion( p.id ) && promotionHasPackageMode( p )
	);

	// Get the first NON-TRADE-UP promotion with package mode to pass to FreePackages component
	const packagePromotion = appliedPromotions.find(
		( p ) => ! isTradeUpPromotion( p.id ) && promotionHasPackageMode( p )
	) || null;

	// Filter packages based on the promotion's availablePackages from the backend
	const availablePackagesForPromotion = packagePromotion?.availablePackages
		? FREE_PACKAGE_OPTIONS.filter( ( pkg ) =>
				packagePromotion.availablePackages.includes( pkg.id )
		  )
		: [];

	// Load form state from IndexedDB on mount
	useEffect( () => {
		const loadState = async () => {
			try {
				const savedState = await offlineStorage.loadFormState();
				if ( savedState ) {
					// Ensure requestType defaults to 'quote' if empty
					const customerInfoWithDefaults = {
						...savedState.customerInfo,
						requestType: savedState.customerInfo.requestType || 'quote',
					};
					// The restored dealer may no longer be valid (event/restriction may
					// have changed since this was saved) — only relevant in the
					// unrestricted case, where the dealer list depends on country.
					if (
						! isPartnerSelectionRestricted &&
						! isDistribuidorValidForCountry( distributors, customerInfoWithDefaults.pais, customerInfoWithDefaults.distribuidor )
					) {
						customerInfoWithDefaults.distribuidor = '';
					}
					setCustomerInfo( customerInfoWithDefaults );
					setCartItems( savedState.cartItems );
					setAppliedPromotions( savedState.appliedPromotions );
					setSelectedFreeResins( savedState.selectedFreeResins );
					if ( savedState.selectedPackage ) {
						setSelectedPackage( savedState.selectedPackage );
					}
					setSignature( savedState.signature );
					setRemarks( savedState.remarks );
				}
			} catch ( error ) {
				console.error( 'Error loading form state:', error );
			}
		};

		loadState();
	}, [] );

	// Save form state to IndexedDB whenever it changes
	useEffect( () => {
		const saveState = async () => {
			const formState: FormState = {
				customerInfo,
				cartItems,
				appliedPromotions,
				selectedFreeResins,
				selectedPackage,
				signature,
				remarks,
				isOffline,
				hasUnsyncedOrders,
			};
			try {
				await offlineStorage.saveFormState( formState );
			} catch ( error ) {
				console.error( '❌ [FORM] Error saving form state:', error );
				// Show a warning to the user about potential data loss
				// but don't interrupt their work with intrusive alerts
				console.warn( '⚠️ [FORM] Auto-save failed - form data may not persist if page is closed' );
			}
		};

		// Skip saving during form reset to prevent race conditions
		if ( ! isResetting ) {
			saveState();
		} else {
			console.log( '⏸️ [FORM] Skipping auto-save during form reset' );
		}
	}, [
		customerInfo,
		cartItems,
		appliedPromotions,
		selectedFreeResins,
		selectedPackage,
		signature,
		remarks,
		isOffline,
		hasUnsyncedOrders,
		isResetting,
	] );

	// Monitor online/offline status
	useEffect( () => {
		const handleOnline = () => {
			setIsOffline( false );
			setShowOfflineIndicator( false );
			syncPendingOrders();
		};

		const handleOffline = () => {
			setIsOffline( true );
			setShowOfflineIndicator( true );
			setTimeout( () => setShowOfflineIndicator( false ), 3000 );
		};

		window.addEventListener( 'online', handleOnline );
		window.addEventListener( 'offline', handleOffline );

		// Check for pending orders
		checkPendingOrders();

		// Cleanup expired completed orders on mount
		offlineStorage.cleanupExpiredOrders().then( ( count ) => {
			if ( count > 0 ) {
				console.log( `🧹 [FORM] Cleaned up ${ count } expired completed orders` );
			}
		} );

		return () => {
			window.removeEventListener( 'online', handleOnline );
			window.removeEventListener( 'offline', handleOffline );
			// Cleanup database connection on component unmount
			offlineStorage.close().catch( ( error ) => {
				console.error( '❌ [FORM] Error closing database on unmount:', error );
			} );
		};
	}, [] );

	// Load past orders on mount
	useEffect( () => {
		const loadPastOrders = async () => {
			try {
				const orders = await offlineStorage.getCompletedOrders();
				setPastOrders( orders.map( ( o ) => ( {
					order: o.value.order,
					savedAt: o.value.savedAt,
					expiresAt: o.value.expiresAt,
					orderId: o.value.orderId,
					synced: o.value.synced,
				} ) ) );
			} catch ( error ) {
				console.error( '❌ [FORM] Error loading past orders:', error );
			}
		};
		loadPastOrders();
	}, [] );

	const checkPendingOrders = async () => {
		// Perform health check first
		const health = await offlineStorage.healthCheck();
		console.log( '🏥 [FORM] Database health check result:', health );

		if ( ! health.healthy ) {
			console.warn( '⚠️ [FORM] Database health check failed:', health.message );
			// Continue anyway - the storage methods will handle connection issues
		}

		const hasPending = await offlineStorage.hasPendingOrders();
		setHasUnsyncedOrders( hasPending );

		// Get the actual count of pending orders
		const pendingOrders = await offlineStorage.getPendingOrders();
		const count = pendingOrders.length;
		setPendingOrdersCount( count );
		console.log( `📊 [FORM] Pending orders count: ${ count }` );

		// Trigger sync if there are pending orders and browser is online
		// This handles the case where user submitted offline then reloaded page while online
		// We check navigator.onLine directly to avoid stale state during initial mount
		if ( hasPending && navigator.onLine ) {
			console.log(
				`🔄 [FORM] Triggering sync on page load for ${ count } pending orders`
			);
			await syncPendingOrders();
		}
	};

	const syncPendingOrders = useCallback( async () => {
		const api = getAPIService();
		try {
			// Get the count before syncing
			const pendingOrders = await offlineStorage.getPendingOrders();
			const count = pendingOrders.length;

			if ( count > 0 ) {
				console.log(
					`🔄 [SYNC] Starting to sync ${ count } orders...`
				);
				setIsSyncing( true );
				setSyncingCount( count );
			}

			const { synced, failed } = await api.syncPendingOrders();

			if ( synced > 0 ) {
				console.log( `✅ [SYNC] Synced ${ synced } pending orders` );
			}
			if ( failed > 0 ) {
				console.error( `❌ [SYNC] Failed to sync ${ failed } orders` );
			}

			// Update pending orders count after sync
			await checkPendingOrders();

			// Turn off syncing state
			setIsSyncing( false );
			setSyncingCount( 0 );
		} catch ( error ) {
			console.error( '❌ [SYNC] Error syncing orders:', error );
			setIsSyncing( false );
			setSyncingCount( 0 );
		}
	}, [] );

	const handleCustomerInfoChange = (
		field: keyof CustomerInfoDataType,
		value: string
	) => {
		setCustomerInfo( ( prev ) => ( { ...prev, [ field ]: value } ) );
		// Clear validation error for this field when user starts typing
		setValidationErrors( ( prev ) => ( {
			...prev,
			[ field ]: false,
		} ) );
	};

	// Country changes only affect the dealer selection when there's no event
	// restriction — when restricted, the dealer list is intentionally decoupled
	// from country, so a country change must never touch `distribuidor`.
	const handleCountryChange = ( newPais: string ) => {
		setCustomerInfo( ( prev ) => {
			if ( isPartnerSelectionRestricted ) {
				return { ...prev, pais: newPais };
			}
			const stillValid = isDistribuidorValidForCountry( distributors, newPais, prev.distribuidor );
			return { ...prev, pais: newPais, distribuidor: stillValid ? prev.distribuidor : '' };
		} );
		setValidationErrors( ( prev ) => ( { ...prev, pais: false, distribuidor: false } ) );
	};

	const handleAddToCart = ( item: CartItem ) => {
		setCartItems( ( prev ) => [ ...prev, item ] );
		// Clear cart validation error when user adds items
		if ( validationErrors.cart ) {
			setValidationErrors( ( prev ) => ( { ...prev, cart: false } ) );
		}
	};

	const handleUpdateQuantity = (
		productId: string,
		quantity: number,
		variant?: string
	) => {
		setCartItems( ( prev ) =>
			prev
				.map( ( item ) => {
					if (
						item.product.id === productId &&
						item.selectedVariant === variant
					) {
						return { ...item, quantity };
					}
					return item;
				} )
				.filter( ( item ) => item.quantity > 0 )
		);
	};

	const handleRemoveItem = ( itemIndex: number ) => {
		setCartItems( ( prev ) => prev.filter( ( _, index ) => index !== itemIndex ) );
	};

	const handleAddPromotion = ( promotion: Promotion ) => {
		// Validate if promotion can be added
		const validationResult = canPromotionBeAdded(
			promotion.id,
			appliedPromotions
		);

		if ( ! validationResult.canAdd ) {
			// Set error message for this promotion
			setPromotionErrors( ( prev ) => ( {
				...prev,
				[ promotion.id ]: validationResult.translationKey || 'promotions.errors.generic',
			} ) );
			return;
		}

		// Clear any existing error for this promotion
		setPromotionErrors( ( prev ) => {
			const newErrors = { ...prev };
			delete newErrors[ promotion.id ];
			return newErrors;
		} );

		setAppliedPromotions( ( prev ) => [ ...prev, promotion ] );
		// Clear cart validation error when user adds promotions
		if ( validationErrors.cart ) {
			setValidationErrors( ( prev ) => ( { ...prev, cart: false } ) );
		}
	};

	const handleRemovePromotion = ( promotionId: string ) => {
		setAppliedPromotions( ( prev ) =>
			prev.filter( ( p ) => p.id !== promotionId )
		);

		// Clear error for this promotion
		setPromotionErrors( ( prev ) => {
			const newErrors = { ...prev };
			delete newErrors[ promotionId ];
			return newErrors;
		} );

		// Also clear selected free resins when removing promotions that include free resins
		const remainingPromotions = appliedPromotions.filter(
			( p ) => p.id !== promotionId
		);
		if ( ! remainingPromotions.some( ( p ) => p.includesFreeResins === true ) ) {
			setSelectedFreeResins( [] );
		}
		// Also clear selected package when removing NON-TRADE-UP promotions with package mode
		// Trade-Up promotions don't enable packages, so removing them shouldn't affect packages
		if ( ! remainingPromotions.some(
			( p ) => ! isTradeUpPromotion( p.id ) && promotionHasPackageMode( p )
		) ) {
			setSelectedPackage( null );
		}
	};

	const handleSelectFreeResin = ( resin: SelectedFreeResin ) => {
		// Add unique ID to handle multiple instances of same resin
		const uniqueResin = {
			...resin,
			uniqueId: `${resin.resinId}_${Date.now()}_${Math.random()}`
		};
		setSelectedFreeResins( ( prev ) => [ ...prev, uniqueResin ] );
	};

	const handleRemoveFreeResin = ( uniqueId: string ) => {
		setSelectedFreeResins( ( prev ) =>
			prev.filter( ( r ) => r.uniqueId !== uniqueId )
		);
	};

	const handleSelectPackage = ( pkg: SelectedPackage ) => {
		setSelectedPackage( pkg );
	};

	const handleRemovePackage = () => {
		setSelectedPackage( null );
	};

	const calculateTotal = useCallback( (): number => {
		let total = 0;

		// Add cart items
		cartItems.forEach( ( item ) => {
			total += item.product.price * item.quantity;
		} );

		// Add promotions (replace individual items with promo prices)
		appliedPromotions.forEach( ( promo ) => {
			total += promo.promoPrice;
		} );

		return total;
	}, [ cartItems, appliedPromotions ] );

	// Memoized signature change handler to prevent signature pad re-initialization
	const handleSignatureChange = useCallback(
		( sig: string ) => {
			setSignature( sig );
			// Clear validation error when user starts signing
			if ( sig && validationErrors.signature ) {
				setValidationErrors( ( prev ) => ( {
					...prev,
					signature: false,
				} ) );
			}
		},
		[ validationErrors.signature ]
	);

	const validateForm = (): boolean => {
		// Clear previous errors
		setValidationErrors( {} );

		// Check required fields
		const errors: {
			distribuidor?: boolean;
			nombre?: boolean;
			apellidos?: boolean;
			direccion?: boolean;
			ciudad?: boolean;
			nombreClinicaLaboratorio?: boolean;
			telefono?: boolean;
			email?: boolean;
			codigoPostal?: boolean;
			pais?: boolean;
			signature?: boolean;
			cart?: boolean;
		} = {};

		// Check all customer info fields
		if ( ! customerInfo.distribuidor ) {
			errors.distribuidor = true;
		}
		if ( ! customerInfo.nombre ) {
			errors.nombre = true;
		}
		if ( ! customerInfo.apellidos ) {
			errors.apellidos = true;
		}
		if ( ! customerInfo.direccion ) {
			errors.direccion = true;
		}
		if ( ! customerInfo.ciudad ) {
			errors.ciudad = true;
		}
		if ( ! customerInfo.nombreClinicaLaboratorio ) {
			errors.nombreClinicaLaboratorio = true;
		}
		if ( ! customerInfo.telefono ) {
			errors.telefono = true;
		}
		if ( ! customerInfo.email ) {
			errors.email = true;
		}
		if ( ! customerInfo.codigoPostal ) {
			errors.codigoPostal = true;
		}
		if ( ! customerInfo.pais ) {
			errors.pais = true;
		}

		if ( Object.keys( errors ).length > 0 ) {
			setValidationErrors( errors );
			setSubmitMessage( {
				type: 'error',
				text: translations.footer.requiredFields,
			} );
			return false;
		}

		// Check email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if ( ! emailRegex.test( customerInfo.email ) ) {
			setValidationErrors( { email: true } );
			setSubmitMessage( {
				type: 'error',
				text: translations.footer.invalidEmail,
			} );
			return false;
		}

		// Check signature
		if ( ! signature ) {
			setValidationErrors( { signature: true } );
			setSubmitMessage( {
				type: 'error',
				text: translations.footer.signatureRequired,
			} );
			return false;
		}

		// Only validate cart if not in lead mode
		if ( customerInfo.requestType !== 'lead' ) {
			// Check if cart is empty
			if ( cartItems.length === 0 && appliedPromotions.length === 0 ) {
				setValidationErrors( { cart: true } );
				setSubmitMessage( {
					type: 'error',
					text: translations.footer.cartEmpty,
				} );
				return false;
			}
		}

		return true;
	};

	const handleSubmit = async () => {
		if ( isSubmittingRef.current ) {
			console.warn( '⚠️ [FORM] Submission already in progress, ignoring duplicate call' );
			return;
		}

		if ( ! validateForm() ) {
			return;
		}

		isSubmittingRef.current = true;
		console.log( '📤 [FORM] Starting form submission...' );
		setIsSubmitting( true );
		setSubmitMessage( null );

		// Log cart items for debugging to verify variant information
		console.log( '📋 [FORM] Cart items being submitted:', cartItems.map( ( item ) => ( {
			productName: item.product.name,
			productId: item.product.id,
			selectedVariant: item.selectedVariant || 'none',
			quantity: item.quantity,
		} ) ) );

		const payload: OrderPayload = {
			customerInfo: { ...customerInfo, isDistribuidorManual },
			cartItems,
			promotions: appliedPromotions,
			freeResins: selectedFreeResins,
			selectedPackage: selectedPackage || undefined,
			signature,
			remarks,
			totalAmount: calculateTotal(),
			timestamp: new Date().toISOString(),
			eventId: props.event_id || null,
			clientOrderId: generateClientOrderId(),
		};

		try {
			console.log( '📤 [FORM] Calling API service...' );
			const api = getAPIService();
			const result = await api.submitOrderWithOfflineFallback( payload );

			console.log( '📤 [FORM] API result:', result );

			if ( result.success ) {
				console.log( '✅ [FORM] Submission successful!' );

				// Save to completed orders for past orders feature
				try {
					const savedAt = await offlineStorage.saveCompletedOrder(
						payload,
						result.orderId,
						! result.savedOffline // synced is true if not saved offline
					);
					console.log( '💾 [FORM] Order saved to completed orders:', savedAt );
					// Refresh past orders list
					const orders = await offlineStorage.getCompletedOrders();
					setPastOrders( orders.map( ( o ) => ( {
						order: o.value.order,
						savedAt: o.value.savedAt,
						expiresAt: o.value.expiresAt,
						orderId: o.value.orderId,
						synced: o.value.synced,
					} ) ) );
				} catch ( error ) {
					console.error( '❌ [FORM] Error saving to completed orders:', error );
					// Don't block submission if this fails
				}

				// Store the submitted order for printing
				setLastSubmittedOrder( payload );
				setShowPrintButton( true );
				// Set timestamp for detecting when user starts new order
				printShownTimestampRef.current = Date.now();

				// Show success message
				setSubmitMessage( {
					type: 'success',
					text: result.savedOffline
						? translations.footer.offlineMessage
						: translations.footer.success,
				} );

				if ( result.savedOffline ) {
					console.log(
						'📴 [FORM] Order saved offline, updating pending count...'
					);
					// Update pending orders count when saved offline
					await checkPendingOrders();
					// Also clear the form after saving offline, preserving campaign selection
					console.log(
						'🧹 [FORM] Clearing form after offline save...'
					);
					await resetForm( true );
					console.log( '✅ [FORM] Form cleared after offline save' );
				} else {
					console.log(
						'🧹 [FORM] Clearing form after successful submission...'
					);
					// Clear form state after successful online submission, preserving campaign selection
					await resetForm( true );
					console.log( '✅ [FORM] Form cleared' );
				}

				// Auto-hide success message after 5 seconds
				setTimeout( () => {
					console.log( '💬 [FORM] Hiding success message' );
					setSubmitMessage( null );
				}, 5000 );
			} else {
				console.error( '❌ [FORM] Submission failed' );
				setSubmitMessage( {
					type: 'error',
					text: translations.footer.error,
				} );
			}
		} catch ( error ) {
			console.error( '❌ [FORM] Error submitting order:', error );
			setSubmitMessage( {
				type: 'error',
				text: translations.footer.error,
			} );
		} finally {
			console.log( '✅ [FORM] Setting isSubmitting to false' );
			isSubmittingRef.current = false;
			setIsSubmitting( false );
		}
	};

	const resetForm = async ( preserveCampaign: boolean = false ) => {
		console.log( '🧹 [FORM] resetForm called', { preserveCampaign } );
		setIsResetting( true ); // Prevent auto-save during reset

		try {
			// Preserve campaign selection if requested
			const preservedCampaign = preserveCampaign
				? {
						hubspotCampaign: customerInfo.hubspotCampaign || '',
						hubspotCampaignName: customerInfo.hubspotCampaignName || '',
				  }
				: {
						hubspotCampaign: '',
						hubspotCampaignName: '',
				  };

			setCustomerInfo( {
				distribuidor: '',
				nombreFachberater: '',
				nombre: '',
				apellidos: '',
				direccion: '',
				ciudad: '',
				nombreClinicaLaboratorio: '',
				telefono: '',
				email: '',
				codigoPostal: '',
				pais: '',
				requestType: 'quote',
				...preservedCampaign,
			} );
			setCartItems( [] );
			setAppliedPromotions( [] );
			setSelectedFreeResins( [] );
			setSelectedPackage( null );
			setSignature( '' );
			setRemarks( '' );
			setValidationErrors( {} );
			// Trigger signature pad clear
			setClearSignatureTrigger( ( prev ) => ! prev );
			console.log( '🧹 [FORM] Clearing offline storage...' );

			try {
				await offlineStorage.clearFormState();
				console.log( '✅ [FORM] Offline storage cleared' );
			} catch ( error ) {
				console.error( '❌ [FORM] Error clearing offline storage:', error );
				// Don't throw - allow form reset to continue even if storage clear fails
			}
		} finally {
			// Small delay to ensure all state updates are processed before re-enabling auto-save
			setTimeout( () => {
				setIsResetting( false );
				console.log( '✅ [FORM] Reset complete, auto-save re-enabled' );
			}, 100 );
		}
	};

	const totalAmount = calculateTotal();

	// Helper to check if form is in lead mode
	const isLeadMode = customerInfo.requestType === 'lead';

	// Get currency for this order (from cart items, promotions, or default to event currency)
	const orderCurrency = (() => {
		if (cartItems.length > 0 && cartItems[0].product.currency) {
			return cartItems[0].product.currency;
		}
		if (appliedPromotions.length > 0 && appliedPromotions[0].currency) {
			return appliedPromotions[0].currency;
		}
		return getEventCurrency();
	})();

	// Past Orders Handlers
	const handleDeletePastOrder = useCallback( async ( savedAt: string ) => {
		try {
			await offlineStorage.deleteCompletedOrder( savedAt );
			// Refresh past orders list
			const orders = await offlineStorage.getCompletedOrders();
			setPastOrders( orders.map( ( o ) => ( {
				order: o.value.order,
				savedAt: o.value.savedAt,
				expiresAt: o.value.expiresAt,
				orderId: o.value.orderId,
				synced: o.value.synced,
			} ) ) );
		} catch ( error ) {
			console.error( '❌ [FORM] Error deleting past order:', error );
		}
	}, [] );

	const handleTogglePastOrdersModal = useCallback( () => {
		setShowPastOrdersModal( ( prev ) => ! prev );
	}, [] );

	return (
		<div className="sprintry-order-form">
			{ /* Offline Banner */ }
			{ showOfflineIndicator && (
				<div className="sprintry-offline-indicator">
					{ translations.footer.offline }:{ ' ' }
					{ translations.footer.offlineMessage }
				</div>
			) }

			{ /* Header */ }
			<Header
				uiText={ translations.header }
				eventName={ eventTitle }
				printTitle={ translations.print.title }
				email={ props.email }
				eventDateRange={ eventDateRange }
			/>

			{ /* Customer Info */ }
			<CustomerInfo
				customerInfo={ customerInfo }
				onChange={ handleCustomerInfoChange }
				onCountryChange={ handleCountryChange }
				dealerSelectionRestricted={ isPartnerSelectionRestricted }
				isDistribuidorManual={ isDistribuidorManual }
				uiText={ translations.customerInfo }
				tradePartnerText={ translations.tradePartner }
				distributorOptions={ filteredDistributors }
				countryOptions={ countryOptions }
				campaignOptions={ campaignOptions }
				campaignsEnabled={ campaignsEnabled }
				validationErrors={ validationErrors }
			/>

			{ /* Product-related sections - hidden in lead mode */ }
			{ ! isLeadMode && (
				<>
					{ /* Products */ }
					<Products
						products={ products }
						cartItems={ cartItems }
						onAddToCart={ handleAddToCart }
						onUpdateQuantity={ handleUpdateQuantity }
						uiText={ translations.products }
					/>

					{ /* Promotions */ }
					<Promotions
						promotions={ promotions }
						appliedPromotions={ appliedPromotions }
						onAddPromotion={ handleAddPromotion }
						onRemovePromotion={ handleRemovePromotion }
						uiText={ translations.promotions }
						promoValidity={ promoValidity }
						promotionErrors={ promotionErrors }
					/>

					{ /* Free Resins */ }
					<FreeResins
						freeResinProducts={ freeResinProducts }
						selectedFreeResins={ selectedFreeResins }
						onSelectFreeResin={ handleSelectFreeResin }
						onRemoveFreeResin={ handleRemoveFreeResin }
						maxBudget={ MAX_FREE_RESINS_BUDGET }
						isEnabled={ isFreeResinsEnabled }
						uiText={ translations.freeResins }
						combos={ combos }
					/>

					{ /* Free Packages (SprintRace Advantage) */ }
					<FreePackages
						availablePackages={ availablePackagesForPromotion }
						selectedPackage={ selectedPackage }
						onSelectPackage={ handleSelectPackage }
						onRemovePackage={ handleRemovePackage }
						appliedPromotion={ packagePromotion }
						isEnabled={ hasPackageMode }
						uiText={ translations.freePackages || {} }
					/>
				 </>
			) }

			{ /* Footer Section */ }
			<section className="sprintry-order-footer">
				<h3 className="sprintry-order-footer__title">
					{ translations.cart.title }
				</h3>

				{ /* Remarks */ }
				<div className="sprintry-form-field">
					<label htmlFor="remarks">
						{ translations.footer.remarks }
					</label>
					<textarea
						id="remarks"
						value={ remarks }
						onChange={ ( e ) => setRemarks( e.target.value ) }
						className="sprintry-form-field__textarea"
						rows={ 4 }
					/>
				</div>

				{ /* Cart Summary and Total - hidden in lead mode */ }
				{ ! isLeadMode && (
					<>
						{ /* Cart Summary */ }
						<CartSummary
							cartItems={ cartItems }
							appliedPromotions={ appliedPromotions }
							selectedFreeResins={ selectedFreeResins }
							selectedPackage={ selectedPackage }
							onRemoveItem={ handleRemoveItem }
							onRemovePromotion={ handleRemovePromotion }
							onRemoveFreeResin={ handleRemoveFreeResin }
							onRemovePackage={ handleRemovePackage }
							uiText={ translations.cart }
						/>

						{ /* Total Amount */ }
						<div className="sprintry-order-footer__total">
							<span className="sprintry-order-footer__total-label">
								Total:
							</span>
							<span className="sprintry-order-footer__total-amount">
								{ getVatDisplayEnabled() ? (
									<span>
										{ formatPrice( calculateGrossPrice( totalAmount, getVatRate() ), orderCurrency ) }
										<span className="sprintry-order-footer__total-amount-label">
											{ translations.footer.vatIncluded || 'incl. VAT' }
										</span>
									</span>
								) : (
									formatPrice( totalAmount, orderCurrency )
								) }
								{ getVatDisplayEnabled() && (
									<span className="sprintry-order-footer__total-vat">
										{ formatPrice( totalAmount, orderCurrency ) } { translations.footer.vatExcluded || 'excl. VAT' }
									</span>
								) }
							</span>
						</div>
					 </>
				) }

				{ /* Disclaimer */ }
				<p className="sprintry-order-footer__disclaimer">
					{ translations.footer.disclaimer }
				</p>

				{ /* VAT Disclaimer - show when VAT display is disabled */ }
				{ ! getVatDisplayEnabled() && (
					<p className="sprintry-vat-disclaimer">
						{ translations.footer.pricesPlusVat || 'Prices plus statutory VAT' }
					</p>
				) }

				{ /* Signature */ }
				<SignaturePadComponent
					onSignatureChange={ handleSignatureChange }
					label={ translations.footer.signature }
					clearLabel={ translations.footer.clearSignature }
					hasError={ !! validationErrors.signature }
					clearSignature={ clearSignatureTrigger }
				/>

				{ /* Submit Message */ }
				{ submitMessage && (
					<div
						className={ `sprintry-order-footer__message sprintry-order-footer__message--${ submitMessage.type }` }
					>
						{ submitMessage.text }
					</div>
				) }

				{ /* Print Button (shown after successful submission) */ }
				{ showPrintButton && lastSubmittedOrder && (
					<div className="sprintry-order-footer__print-section">
						<button
							type="button"
							className="sprintry-order-footer__print-btn"
							onClick={ handlePrintClick }
						>
							{ translations.print.printButton }
						</button>
						<button
							type="button"
							className="sprintry-order-footer__dismiss-btn"
							onClick={ handleDismissPrintSection }
						>
							{ translations.print.closeButton }
						</button>
					</div>
				) }

				{ /* Submit Button */ }
				<button
					type="button"
					className="sprintry-order-footer__submit"
					onClick={ handleSubmit }
					disabled={ isSubmitting }
				>
					{ isSubmitting
						? translations.footer.submitting
						: translations.footer.submitOrder }
				</button>
			</section>

			{ /* Connection Status Indicator */ }
			<div
				className={ `sprintry-connection-indicator ${
					isOffline
						? 'sprintry-connection-indicator--offline'
						: 'sprintry-connection-indicator--online'
				} ${
					isSyncing ? 'sprintry-connection-indicator--syncing' : ''
				}` }
			>
				<div className="sprintry-connection-indicator__dot" />
				<span className="sprintry-connection-indicator__text">
					{ isOffline
						? `${ translations.footer.offline } (${ pendingOrdersCount } ${
								pendingOrdersCount === 1
									? translations.footer.pendingOrder
									: translations.footer.pendingOrders
						  })`
						: isSyncing
						? `${ translations.footer.syncing } ${ syncingCount } ${
								syncingCount === 1 ? translations.footer.pendingOrder : translations.footer.pendingOrders
						  }...`
						: translations.footer.online }
				</span>
			</div>

			{ /* Hidden Print Template - react-to-print will clone this into an iframe */ }
			{ lastSubmittedOrder && (
				<div style={ { display: 'none' } }>
					<OrderPrintTemplate
						ref={ contentRef }
						order={ lastSubmittedOrder }
						eventName={ eventTitle }
						translations={ translations }
						title={ translations.print.title }
						email={ props.email }
					/>
				</div>
			) }

			{ /* Past Orders Floating Button */ }
			<PastOrdersButton
				count={ pastOrders.length }
				onClick={ handleTogglePastOrdersModal }
			/>

			{ /* Past Orders Modal */ }
			<PastOrdersModal
				isOpen={ showPastOrdersModal }
				onClose={ handleTogglePastOrdersModal }
				completedOrders={ pastOrders }
				onDeleteOrder={ handleDeletePastOrder }
				translations={ translations.pastOrders }
				printTranslations={ translations.print }
				eventName={ eventTitle }
			/>
		</div>
	);
};

export default SprintRayOrderForm;
